/**
 * Device ID Utility
 *
 * Provides stable device identification for FCM token management.
 * Uses localStorage to persist a unique device ID that survives:
 * - Page refreshes
 * - FCM token rotations
 * - Browser updates
 *
 * Each browser profile gets its own deviceId, which is the correct behavior
 * since each profile can have different notification preferences.
 */

const DEVICE_ID_KEY = 'fcm_device_id';

/**
 * Gets or creates a stable device ID.
 *
 * If no deviceId exists in localStorage, generates a new UUID and stores it.
 * This ID persists across sessions and FCM token changes.
 *
 * @returns The device ID (always returns a valid UUID)
 */
export function getOrCreateDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Gets the current device ID without creating one.
 *
 * Useful for checking if we're on a previously registered device
 * without triggering a new ID generation.
 *
 * @returns The device ID if it exists, null otherwise
 */
export function getCurrentDeviceId(): string | null {
  return localStorage.getItem(DEVICE_ID_KEY);
}
